/* Ürün sayfası zengin içerik etkileşimleri. Sahibi: ürün sayfası ekibi.
   Loaded only on product pages, after theme.js (both deferred). It does five things:
   1. reveal: [data-pr-reveal] blocks drift into place as they scroll in (siblings 90 ms apart); photos marked
      .pr-mask open behind a lifting curtain (CSS) when they get .is-in;
   2. parallax: band photos ([data-pr-parallax]) move a little slower than the page while on screen;
   3. sideways tracks ([data-pr-carousel]): the dots / progress line follow the swipe;
   4. accordions (details.acc): short panels open and close with a height animation; long ones (the full
      ingredient list) open at once and only their text fades in, so the page is not re-laid out every frame;
   5. picking another shade swaps the product section in place (theme.js); the rhode sections below
      (.shopify-section.section-pr) are then re-rendered for the new shade with the Section Rendering API.
   The "+" buttons and "Add all" use theme.js ([data-quick-add] / [data-add-set]); nothing here touches the bag.
   In the theme editor a re-drawn section (shopify:section:load) is set up again.
   Everything that moves uses transform / opacity, and prefers-reduced-motion turns the motion off. */
(() => {
  const doc = document;
  const root = doc.documentElement;
  const $ = (sel, ctx = doc) => ctx.querySelector(sel);
  const $$ = (sel, ctx = doc) => Array.from(ctx.querySelectorAll(sel));
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const reduced = () => motion.matches;
  const hasIO = 'IntersectionObserver' in window;
  const safely = (fn) => { try { fn(); } catch (e) { console.warn(e); } };

  root.classList.add('pr-js');

  /* ---------- 1. reveal ---------- */
  let revealIO = null;
  function reveal(scope) {
    const all = $$('[data-pr-reveal]:not(.is-in)', scope);
    // cards inside a sideways track are shown at once: the track itself fades in, so swiping never shows empty cards
    const inTrack = all.filter((el) => el.parentElement && el.parentElement.closest('[data-pr-track]'));
    inTrack.forEach((el) => el.classList.add('is-in'));
    const els = all.filter((el) => !inTrack.includes(el));
    const seen = new Map();
    els.forEach((el) => {
      const parent = el.parentElement;
      const index = seen.get(parent) || 0;
      seen.set(parent, index + 1);
      el.style.setProperty('--rd', `${Math.min(index, 6) * 90}ms`);
    });
    if (reduced() || !hasIO) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    els.forEach((el) => observer().observe(el));
  }
  function observer() {
    if (!revealIO) {
      revealIO = new IntersectionObserver((entries) => entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        revealIO.unobserve(entry.target);
      }), { rootMargin: '0px 0px -6% 0px', threshold: 0.08 });
    }
    return revealIO;
  }
  // the theme's own .reveal blocks (theme.js shows them once on page load) in a re-rendered section: without this they
  // stay invisible after another shade is picked and the page below the buy area looks empty (Arda, 22 Eki)
  function revealPlain(scope) {
    const els = $$('.reveal:not(.is-in)', scope);
    if (reduced() || !hasIO) { els.forEach((el) => el.classList.add('is-in')); return; }
    els.forEach((el) => observer().observe(el));
  }

  /* ---------- 2. parallax (band photos) ---------- */
  const moving = new Set();
  let paraIO = null;
  let ticking = false;
  function paint() {
    ticking = false;
    const vh = window.innerHeight || 1;
    moving.forEach((img) => {
      const box = img.parentElement.getBoundingClientRect();
      const centre = (box.top + box.height / 2 - vh / 2) / (vh / 2 + box.height / 2);
      const y = Math.max(-1, Math.min(1, centre)) * -30;
      img.style.transform = `translate3d(0, ${y.toFixed(1)}px, 0) scale(1.12)`;
    });
  }
  function requestPaint() {
    if (ticking || !moving.size) return;
    ticking = true;
    requestAnimationFrame(paint);
  }
  function parallax(scope) {
    if (reduced() || !hasIO) return;
    if (!paraIO) {
      paraIO = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) moving.add(entry.target);
          else moving.delete(entry.target);
        });
        requestPaint();
      }, { rootMargin: '15% 0px' });
      window.addEventListener('scroll', requestPaint, { passive: true });
      window.addEventListener('resize', requestPaint, { passive: true });
    }
    $$('[data-pr-parallax]', scope).forEach((img) => paraIO.observe(img));
  }
  function forget(scope) {
    $$('[data-pr-parallax]', scope).forEach((img) => {
      moving.delete(img);
      if (paraIO) paraIO.unobserve(img);
    });
    if (revealIO) $$('[data-pr-reveal]', scope).forEach((el) => revealIO.unobserve(el));
  }

  /* ---------- 3. sideways tracks: dots + progress line ---------- */
  function carousels(scope) {
    $$('[data-pr-carousel]', scope).forEach((box) => {
      const track = $('[data-pr-track]', box);
      if (!track || track.dataset.prReady) return;
      track.dataset.prReady = '1';
      const dots = $$('.pr-dots i', box);
      const bar = $('[data-pr-bar]', box);
      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        const x = Math.abs(track.scrollLeft);
        if (dots.length) {
          const index = Math.round(x / Math.max(1, track.clientWidth));
          dots.forEach((dot, i) => dot.classList.toggle('is-on', i === Math.min(index, dots.length - 1)));
        }
        if (bar) {
          bar.parentElement.classList.toggle('is-static', max <= 2);
          const seen = track.clientWidth / Math.max(1, track.scrollWidth);
          const part = max > 0 ? seen + (1 - seen) * (x / max) : 1;
          bar.style.setProperty('--p', Math.min(1, Math.max(0.05, part)).toFixed(3));
        }
      };
      let frame = 0;
      track.addEventListener('scroll', () => {
        if (frame) return;
        frame = requestAnimationFrame(() => { frame = 0; update(); });
      }, { passive: true });
      window.addEventListener('resize', update, { passive: true });
      update();
    });
  }

  /* ---------- 4. accordions: height animation ---------- */
  // slow and soft, like the rest of the page (0.5–0.65 s ease-out)
  const EASE = 'cubic-bezier(.16, 1, .3, 1)';
  function accordions(scope) {
    $$('details.acc', scope).forEach((item) => {
      if (item.dataset.prAcc) return;
      item.dataset.prAcc = '1';
      const summary = $('summary', item);
      const panel = summary && summary.nextElementSibling;
      if (!panel || !panel.animate) return;
      summary.addEventListener('click', (e) => {
        if (reduced()) return;
        e.preventDefault();
        if (item.prAnim) item.prAnim.cancel();
        const pad = getComputedStyle(panel).paddingBottom;
        panel.style.overflow = 'hidden';
        const done = () => { panel.style.overflow = ''; item.prAnim = null; };
        if (item.open) {
          if (panel.offsetHeight > 300) { item.open = false; done(); return; }
          item.classList.add('is-closing');
          const anim = panel.animate([
            { height: `${panel.offsetHeight}px`, paddingBottom: pad, opacity: 1 },
            { height: '0px', paddingBottom: '0px', opacity: 0 }
          ], { duration: 520, easing: EASE });
          item.prAnim = anim;
          anim.onfinish = () => { item.open = false; item.classList.remove('is-closing'); done(); };
          anim.oncancel = () => item.classList.remove('is-closing');
        } else {
          item.open = true;
          if (panel.offsetHeight > 300) { done(); return; } // long panel: open at once, the text fades in (CSS accIn)
          const anim = panel.animate([
            { height: '0px', paddingBottom: '0px', opacity: 0 },
            { height: `${panel.offsetHeight}px`, paddingBottom: pad, opacity: 1 }
          ], { duration: 650, easing: EASE });
          item.prAnim = anim;
          anim.onfinish = done;
        }
      });
    });
  }

  function init(scope) {
    safely(() => window.STREReviews && window.STREReviews(scope));
    safely(() => reveal(scope));
    safely(() => parallax(scope));
    safely(() => carousels(scope));
    safely(() => accordions(scope));
  }

  /* ---------- 5. another shade picked: re-render the rhode sections ---------- */
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
  let syncToken = 0;
  async function syncSections(path) {
    const hosts = $$('.shopify-section.section-pr');
    if (!hosts.length) return;
    const token = ++syncToken;
    const ids = hosts.map((host) => host.id.replace(/^shopify-section-/, ''));
    hosts.forEach((host) => host.classList.add('pr-busy'));
    try {
      // the Section Rendering API takes at most 5 sections per request
      for (let i = 0; i < ids.length; i += 5) {
        const chunk = ids.slice(i, i + 5);
        const res = await fetch(`${path}?sections=${chunk.map(encodeURIComponent).join(',')}`);
        if (!res.ok) throw new Error(res.statusText);
        const data = await res.json();
        if (token !== syncToken) return;
        chunk.forEach((id) => {
          const host = doc.getElementById(`shopify-section-${id}`);
          if (!host || typeof data[id] !== 'string') return;
          const fresh = parse(data[id]).getElementById(`shopify-section-${id}`);
          forget(host);
          host.innerHTML = fresh ? fresh.innerHTML : '';
          init(host);
          safely(() => revealPlain(host));
        });
      }
    } catch (e) {
      console.warn(e);
    } finally {
      if (token === syncToken) hosts.forEach((host) => host.classList.remove('pr-busy'));
    }
  }

  function watchShadeSwap() {
    const pdp = $('[data-product]');
    const host = pdp && pdp.parentElement;
    if (!host || !('MutationObserver' in window)) return;
    let current = pdp;
    new MutationObserver(() => {
      const now = $('[data-product]', host);
      if (!now || now === current) return;
      current = now;
      // theme.js has already put the new address in the bar (history.replaceState) and set up the new section
      safely(() => accordions(now));
      syncSections(location.pathname);
    }).observe(host, { childList: true });
  }

  init(doc);
  safely(watchShadeSwap);
  // theme editor: a section re-drawn after a setting change gets its motion and tracks back
  doc.addEventListener('shopify:section:load', (e) => init(e.target));
})();

/* "meet <shade>" row (Apple edition, 22 Eki): an endless row — copies of the photos sit before and after the real ones,
   and once a swipe ends in a copy the row jumps back by one set without animation, so it never reaches an end
   (Arda: "sağa kaydırınca başa sarsın, sınırsız"). The "swipe" badge leaves once the row is moved; the arrow scrolls one
   photo on. With reduced motion there is no jump animation either way.
   The copies before the real photos stay invisible until the row is first moved, so it opens like a fresh start with
   an empty left edge (Arda: "sol boş olsun, kaydırdıkça solda gözüksün"). */
(() => {
  const reduce = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const init = (sec) => {
    const row = sec.querySelector('[data-meet-row]');
    if (!row || row.dataset.ready) return;
    row.dataset.ready = '1';
    const swipe = sec.querySelector('[data-meet-swipe]');
    const next = sec.querySelector('[data-meet-next]');
    const slides = [...row.querySelectorAll('[data-meet-slide]')];
    const dir = document.documentElement.dir === 'rtl' ? -1 : 1;
    if (slides.length < 2) return;
    const copy = (s) => {
      const c = s.cloneNode(true);
      c.setAttribute('aria-hidden', 'true');
      c.removeAttribute('data-meet-slide');
      c.querySelectorAll('img').forEach((img) => { img.loading = 'lazy'; img.alt = ''; });
      return c;
    };
    const before = slides.map(copy);
    before.forEach((c) => c.setAttribute('data-meet-copy', 'before'));
    const after = slides.map(copy);
    row.prepend(...before);
    row.append(...after);
    let setWidth = 0;
    let ready = false;
    const measure = () => { setWidth = Math.abs(after[0].getBoundingClientRect().left - slides[0].getBoundingClientRect().left); };
    const jump = (x) => {
      row.style.scrollSnapType = 'none';
      row.scrollLeft = x;
      void row.offsetWidth;
      row.style.scrollSnapType = '';
    };
    const start = () => { measure(); jump(dir * setWidth); setTimeout(() => { ready = true; }, 120); };
    if (document.readyState === 'complete') requestAnimationFrame(start);
    else window.addEventListener('load', () => requestAnimationFrame(start), { once: true });
    window.addEventListener('resize', () => { const set = Math.round(Math.abs(row.scrollLeft) / (setWidth || 1)); measure(); jump(dir * setWidth * Math.max(1, Math.min(set, 1))); });

    // after a swipe settles: from a copy back into the real set
    const settle = () => {
      if (!setWidth) return;
      const pos = Math.abs(row.scrollLeft);
      if (pos < setWidth * 0.5) jump(dir * (pos + setWidth));
      else if (pos >= setWidth * 1.5) jump(dir * (pos - setWidth));
    };
    let timer = 0;
    row.addEventListener('scroll', () => {
      if (!ready) return;
      swipe?.classList.add('is-gone');
      row.classList.add('is-moved');
      clearTimeout(timer);
      timer = setTimeout(settle, 140);
    }, { passive: true });
    if ('onscrollend' in window) row.addEventListener('scrollend', () => { clearTimeout(timer); settle(); });

    next?.addEventListener('click', () => {
      const step = slides[0].getBoundingClientRect().width + 12;
      row.scrollBy({ left: dir * step, behavior: reduce() ? 'auto' : 'smooth' });
    });
  };
  const all = (scope = document) => scope.querySelectorAll('[data-meet]').forEach(init);
  all();
  // a shade picked in place swaps the sections: set the new row up as well
  new MutationObserver(() => all()).observe(document.querySelector('main') || document.body, { childList: true, subtree: true });
})();
